import React from 'react'
import { CONTACT_CONFIG } from '../../config/constants.js'
import { packagesFor, BOOKING_LABEL, CHAT_LABEL, COPY_LABEL, type CopyLang } from '../../data/workshop-packages.js'
import { useLocale } from '../../hooks/i18n/useLocale.js'
import { useLandingContent } from '../../hooks/i18n/usePageContent.js'
import * as TOKENS from '../chat/design-tokens'

interface WorkshopsSectionProps {
  workshopsRef: React.RefObject<HTMLElement>
}

type AccentKey = 'copper'

interface AccentTokens {
  badge: string
  dot: string
}

const ACCENT_MAP: Record<AccentKey, AccentTokens> = {
  copper: {
    badge: `${TOKENS.SEMANTIC.orangeBg} ${TOKENS.SEMANTIC.orangeText} border ${TOKENS.BRAND.accentBorder}`,
    dot: TOKENS.BRAND.accentBg,
  },
}

const sectionShell = `w-full scroll-mt-24 px-6 py-20 md:px-12 border-t ${TOKENS.BORDER.default} bg-transparent`
const eyebrow = `${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`

export const WorkshopsSection: React.FC<WorkshopsSectionProps> = ({
  workshopsRef,
}) => {
  const { workshops } = useLandingContent()
  const { locale } = useLocale()
  const lang: CopyLang = locale === 'nb' ? 'nb' : 'en'
  const cards = packagesFor('landing', lang)
  const accent = ACCENT_MAP.copper
  const booking = CONTACT_CONFIG.SCHEDULING.BOOKING_URL

  return (
    <section
      ref={workshopsRef}
      id="workshops"
      aria-labelledby="workshops-heading"
      className={`${sectionShell} relative overflow-hidden`}
    >
      <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-14">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="flex max-w-2xl flex-col gap-3">
            <span className={eyebrow}>{workshops.eyebrow}</span>
            <h2
              id="workshops-heading"
              className={`text-4xl font-semibold tracking-tighter md:text-5xl ${TOKENS.TEXT.primary}`}
            >
              {workshops.title}
            </h2>
            <p className={`text-lg font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
              {workshops.body}
            </p>
          </div>
          <a
            href="/workshops"
            className={`
              group relative shrink-0 inline-flex items-center justify-center gap-2
              overflow-hidden self-start rounded-none border px-6 py-3
              ${TOKENS.TEXT_SIZE.sm} font-medium tracking-wide
              ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.raised}
              transition-colors duration-150 hover:bg-muted hover:border-foreground/30
              ${TOKENS.INTERACTION.focusRing}
            `}
          >
            <span className={TOKENS.INTERACTION.shimmer} />
            <span className="relative">{workshops.cta}</span>
          </a>
        </div>

        <div className="flex flex-col border-t border-border" role="list">
          {cards.map(ws => (
            <article
              key={ws.id}
              role="listitem"
              className={`
                group flex flex-col gap-6 border-b py-8
                ${TOKENS.BORDER.default}
                transition-colors duration-150 hover:bg-secondary
                md:flex-row md:gap-12
              `}
            >
              <div className="flex w-full shrink-0 flex-col gap-3 md:w-56">
                <span
                  className={`
                    inline-flex w-fit border px-3 py-1
                    ${TOKENS.TEXT_SIZE.xs} font-bold uppercase tracking-wider
                    ${accent.badge}
                  `}
                >
                  {ws.audience}
                </span>
                <span className={eyebrow}>
                  {workshops.durationLabel} {ws.format}
                </span>
              </div>

              <div className="flex flex-1 flex-col gap-4">
                <h3 className={`text-xl font-semibold tracking-tight ${TOKENS.TEXT.primary}`}>
                  <a href={`/workshops#${ws.anchor}`}>{ws.name}</a>
                </h3>
                <p className={`max-w-2xl text-base font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
                  {ws.summary}
                </p>
                <h4 className={eyebrow}>{COPY_LABEL.modules[lang]}</h4>
                <ul className="flex max-w-2xl flex-col gap-3">
                  {ws.modules.map(mod => (
                    <li key={mod.id} className="flex flex-col gap-1">
                      <span className={`font-mono ${TOKENS.TEXT_SIZE.xs} ${TOKENS.TEXT.primary}`}>
                        {mod.name}
                        {mod.note ? ` (${mod.note})` : ''}
                      </span>
                      <span className={`text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>{mod.content}</span>
                    </li>
                  ))}
                </ul>
                <h4 className={eyebrow}>{COPY_LABEL.gets[lang]}</h4>
                <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                  {ws.points.map(b => (
                    <li
                      key={b}
                      className={`inline-flex items-center gap-2 ${TOKENS.TEXT_SIZE.xs} font-mono tracking-wide ${TOKENS.TEXT.muted}`}
                    >
                      <span className={`h-1 w-1 shrink-0 rounded-full ${accent.dot}`} aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
                <p className={`max-w-2xl text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
                  {ws.notIncluded}
                </p>
                {ws.next ? (
                  <p className={`max-w-2xl text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>{ws.next}</p>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-8 pt-2 md:grid-cols-3">
          {workshops.features.map(f => (
            <div key={f.title} className="flex flex-col gap-2">
              <h4 className={`${TOKENS.TEXT_SIZE.xs} font-semibold uppercase tracking-wide ${TOKENS.TEXT.primary}`}>
                {f.title}
              </h4>
              <p className={`text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
                {f.body}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center justify-center gap-3 pt-6 sm:flex-row">
          <a href="/chat" className={`group relative overflow-hidden ${TOKENS.PATTERNS.marketingPrimaryLarge}`}>
            <span className={TOKENS.INTERACTION.shimmer} />
            <span className="relative">{CHAT_LABEL[lang]}</span>
          </a>
          <a
            href={booking}
            target="_blank"
            rel="noopener noreferrer"
            className={`group relative overflow-hidden ${TOKENS.PATTERNS.marketingPrimaryLarge}`}
          >
            <span className={TOKENS.INTERACTION.shimmer} />
            <span className="relative">{BOOKING_LABEL[lang]}</span>
            <svg
              className="relative h-4 w-4 transition-transform duration-150 group-hover:translate-x-1"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  )
}
